import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgeCheck, CheckCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
// 경계 규칙(Component→Hook→Service) 명시적 예외: useDepositAccountStatus 훅은 마운트 즉시
// 무조건 fetch라 '미해제일 때만 조회' 게이트와 충돌 — 1회성 팝업 하나를 위한 조건부 fetch 훅
// 신설 대신 service 함수를 직접 호출한다 (repository/클라이언트 직접 접근은 아님).
import { getDepositAccountConnectionStatus } from "@/supabase/deposit/service/depositAccountService";
import { hasOrdersSince } from "@/supabase/order/service/orderService";
import { hasParsedOrdersSince } from "@/supabase/parsedorder/parsedOrderService";
import { DEPOSIT_BANKS } from "@/supabase/deposit/banks";
import { subDays } from "date-fns";

const STORAGE_KEY = "deposit_launch_notice_dismissed";
// 타겟팅: 최근 30일 내 정산서(order) 또는 자동정리(parsed_order) 사용 이력이 있는 활성 유저에게만 노출.
const RECENT_USAGE_DAYS = 30;

// localStorage 접근이 차단된 환경(프라이버시 모드 등)에서 effect/CTA가 죽지 않도록 가드.
function safeGetDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    // 저장소를 못 읽으면 노출하지 않는 쪽이 안전 — '다시 보지 않기'도 저장 불가라 재노출 루프가 되기 때문.
    return true;
  }
}

function safeSetDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // 저장 실패는 무시 — 이번 세션의 노출 억제는 isOpen state가 담당.
  }
}

// 지원 은행 목록은 banks.ts 단일 소스에서 파생 (하드코딩 분산 방지).
const SUPPORTED_BANKS = DEPOSIT_BANKS.filter((bank) => bank.onboardingAvailable);

export function DepositLaunchModal() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // 로그인 + 미해제 상태에서만 검사. dep은 userId — user 객체 dep이면 토큰 리프레시마다
    // 재실행되어 '닫기' 후 같은 세션에서 재노출됨 (닫기=다음 접속 시 재노출 계약 위반).
    if (!userId) return;
    if (safeGetDismissed()) return;
    // 이미 /deposits에 있는 유저에겐 이중 소개하지 않는다 — 페이지 인트로가 같은 역할.
    // (트레일링 슬래시 정규화: React Router는 /deposits/ 도 같은 라우트로 매칭)
    if (window.location.pathname.replace(/\/+$/, "") === "/deposits") return;

    let cancelled = false;
    void (async () => {
      // 사용 이력 게이트: 최근 30일 내 정산서/자동정리 이력 없는 유저는 이번 세션 노출 보류.
      // 영구 해제가 아님 — 나중에 기능을 쓰기 시작하면 그때 노출된다. 판별 실패 시에도 보류(오타겟 방지).
      try {
        // created_at 기준 — order_date는 수정 가능한 영업일자라 활동 판별에 부적합
        const sinceIso = subDays(new Date(), RECENT_USAGE_DAYS).toISOString();
        const isActiveUser =
          (await hasOrdersSince(userId, sinceIso)) ||
          (await hasParsedOrdersSince(userId, sinceIso));
        if (cancelled || !isActiveUser) return;
      } catch {
        return;
      }

      try {
        const status = await getDepositAccountConnectionStatus();
        if (cancelled) return;
        if (status === "linked" || status === "in_progress") {
          // 이미 연동을 시작/완료한 유저에겐 불필요 — 조용히 영구 해제 (이후 재조회 없음)
          safeSetDismissed();
          return;
        }
        setIsOpen(true);
      } catch {
        // 상태 조회 실패 시 미연동 가정으로 노출 (fail-open)
        if (!cancelled) setIsOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    safeSetDismissed();
    setIsOpen(false);
  };

  const handleGoToDeposits = () => {
    safeSetDismissed();
    setIsOpen(false);
    navigate("/deposits");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg"
        // 첫 포커스가 '다시 보지 않기'(DOM상 첫 버튼)에 잡혀 테두리 박스처럼 보이거나(링),
        // 하단 CTA 포커스로 overflow 상태에서 열리자마자 아래로 스크롤되는 것 방지 —
        // 상단 제목(tabIndex=-1)에 포커스: 제목부터 보이고, Tab 시 첫 버튼에 focus-visible 링 정상 표시.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader className="space-y-3 text-left">
          {/* 시각 앵커: 아이콘 배지 + NEW — 출시 공지임을 텍스트 이전에 전달 */}
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
              <BadgeCheck className="h-5 w-5 text-slate-700" />
            </span>
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold tracking-wide text-white">
              NEW
            </span>
          </div>
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="break-keep text-xl font-bold leading-snug text-slate-900 outline-none"
          >
            자동 입금 매칭 기능이 출시되었습니다
          </DialogTitle>
          {/* asChild: Radix Description은 <p>라 내부 <div>가 invalid nesting — div로 대체 */}
          <DialogDescription asChild>
            <div className="space-y-4 pt-1 text-left">
              <p className="break-keep text-[15px] leading-relaxed text-slate-600">
                <strong className="font-semibold text-slate-900">계좌를 한 번만 연동</strong>
                하면 입금될 때마다 정산서와 연동되고,{" "}
                <strong className="font-semibold text-slate-900">
                  입금 완료 처리를 수월하게
                </strong>{" "}
                할 수 있습니다.
              </p>
              {/* 핵심 베네핏 문장 = 하이라이트 박스 승격 (훑어봐도 이 줄은 읽힘) */}
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3.5 py-3 ring-1 ring-inset ring-slate-200/70">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <p className="break-keep text-sm font-medium leading-relaxed text-slate-800">
                  일일이 수기로 입금을 확인하지 않아도 됩니다.
                </p>
              </div>
              {/* 은행 칩: '지원' 고아 단어 → 그룹 캡션으로 승격 */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">지원 은행</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUPPORTED_BANKS.map((bank) => (
                    <span
                      key={bank.code}
                      className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                    >
                      {bank.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          {/* tertiary: 텍스트 링크 수준 강등 — primary와 시각 경쟁 제거 */}
          <Button
            type="button"
            variant="ghost"
            onClick={handleDontShowAgain}
            className="h-auto self-center px-2 py-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700 sm:self-auto"
          >
            다시 보지 않기
          </Button>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button type="button" variant="outline" onClick={handleClose} className="w-full sm:w-auto">
              닫기
            </Button>
            <Button
              type="button"
              onClick={handleGoToDeposits}
              className="w-full sm:w-auto bg-slate-900 px-5 text-white hover:bg-slate-800"
            >
              지금 연동하기
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
